import { AdminMe, CatalogResponse, MeResponse, Series, SiteVisuals } from "./types";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers || {})
    }
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string };
    throw Object.assign(new Error(body.error || `Request failed: ${response.status}`), { status: response.status, body });
  }
  return response.json() as Promise<T>;
}

export function getMe() {
  return request<MeResponse>("/api/me");
}

export function getCatalog() {
  return request<CatalogResponse>("/api/catalog");
}

export function getSiteVisuals() {
  return request<{ visuals: SiteVisuals; storage: "static" | "default" | "d1" }>("/api/site-visuals");
}

export function getEpisode(slug: string) {
  return request<{ episode: CatalogResponse["episodes"][number]; related: Array<{ slug: string; title: string; release_date: string; tier_required: string; effectiveTierRequired?: string; accessBadgeLabel?: string; unlocked: boolean }> }>(`/api/episode/${encodeURIComponent(slug)}`);
}

export function getEpisodeLink(slug: string) {
  return request<{ url: string; slug: string; postId: string; tierRequired: string }>(`/api/episode-link/${encodeURIComponent(slug)}`);
}

export function refreshAuth() {
  return request<MeResponse & { note?: string }>("/api/auth/refresh", { method: "POST" });
}

export function mockLogin(state: MeResponse["accessLevel"]) {
  return request<MeResponse>(`/api/dev/mock-login?state=${encodeURIComponent(state)}`, { method: "POST" });
}

export function logout() {
  return request<MeResponse>("/api/auth/logout", { method: "POST" });
}

export function getAdminMe() {
  return request<AdminMe>("/api/admin/me");
}

export function adminLogin(password: string) {
  return request<AdminMe>("/api/admin/login", { method: "POST", body: JSON.stringify({ password }) });
}

export function adminLogout() {
  return request<{ authenticated: false }>("/api/admin/logout", { method: "POST" });
}

export function getAdminDashboard() {
  return request<{
    storage: string;
    d1Bound: boolean;
    r2Bound: boolean;
    kvPrivateLinksBound: boolean;
    counts: Record<string, number>;
    tierDistribution: Record<string, number>;
    effectiveTierDistribution: Record<string, number>;
    tierAging: {
      currentCatalogDate: string;
      platinumAgingCutoff: string;
      progressiveCuts: number;
      freshPlatinumEarlyAccess: number;
      allPaidMembers: number;
      missingReleaseDates: number;
      nextAllPaidUnlocks: Array<{ slug: string; title: string; release_date: string; unlocksOn: string | null }>;
      missingReleaseDateEpisodes: Array<{ slug: string; title: string; release_date: string }>;
    };
    buttonActionHealth: {
      cardPrimaryActions: number;
      detailPrimaryActions: number;
      adminButtonsModeled: boolean;
      knownNoOpButtons: number;
      model: string;
    };
    latestReleaseDate: string | null;
    latest: CatalogResponse["episodes"];
    lowConfidence: CatalogResponse["episodes"];
    missingThumbnails: CatalogResponse["episodes"];
    placeholderImages: CatalogResponse["episodes"];
    multiplePossibleImages: CatalogResponse["episodes"];
    missingTier: CatalogResponse["episodes"];
    missingDescriptions: CatalogResponse["episodes"];
    missingPatreonPostUrls: CatalogResponse["episodes"];
    missingSeriesCovers: CatalogResponse["series"];
    unmapped: CatalogResponse["episodes"];
    assetSync: Record<string, unknown>;
    tierMapStatus: Record<string, unknown>;
    patreonAgingStatus: Record<string, unknown>;
    catalogSync: Record<string, unknown>;
    meta: Record<string, unknown>;
  }>("/api/admin/dashboard");
}

export function getAdminCatalog() {
  return request<CatalogResponse>("/api/admin/catalog");
}

export function getAdminSiteVisuals() {
  return request<{ visuals: SiteVisuals; storage: string; writable: boolean }>("/api/admin/site-visuals");
}

export function saveAdminSiteVisuals(csrf: string, body: SiteVisuals) {
  return request<{ saved: boolean; visuals: SiteVisuals; storage: string }>("/api/admin/site-visuals", {
    method: "PATCH",
    headers: { "x-csrf-token": csrf },
    body: JSON.stringify(body)
  });
}

export interface AdminPatreonSyncStatus {
  configured: boolean;
  state?: "ready" | "not_configured" | string;
  message?: string;
  missing?: string[];
  owner?: string;
  repo?: string;
  workflowId?: number;
  workflowFile?: string;
  workflowPath?: string;
  workflowState?: string;
  githubStage?: string;
  githubRequestPath?: string;
  ref?: string;
  inputKeys?: string[];
  actionUrl?: string;
  setupSteps?: string[];
  tokenPresent?: boolean;
  started?: boolean;
  status?: string;
  dryRun?: boolean;
  mode?: "dry-run" | "sync" | "sync-deploy" | string;
  latestRun?: {
    id?: number | string;
    name?: string;
    event?: string;
    status?: string;
    conclusion?: string | null;
    run_started_at?: string;
    updated_at?: string;
    html_url?: string;
    failedJob?: { name?: string; html_url?: string; conclusion?: string } | null;
    failedStep?: { name?: string; number?: number | string; conclusion?: string } | null;
    nextAction?: string;
  } | null;
  errorStatus?: number;
  error?: string;
  githubError?: {
    status?: number;
    message?: string;
    documentation_url?: string;
    errors?: unknown;
  };
}

export function getAdminPatreonSyncStatus() {
  return request<AdminPatreonSyncStatus>("/api/admin/patreon-sync");
}

export interface AdminGithubHealth {
  configured: boolean;
  ready: boolean;
  status: string;
  state?: string;
  message: string;
  requiredSecret: string;
  requiredPermission: string;
  missing?: string[];
  owner?: string;
  repo?: string;
  ref?: string;
  path?: string;
  setupSteps?: string[];
  tokenPresent?: boolean;
  fileExists?: boolean;
  githubStage?: string;
  githubRequestPath?: string;
  errorStatus?: number;
  githubError?: {
    status?: number;
    message?: string;
    documentation_url?: string;
    acceptedPermissions?: string;
  };
}

export function getAdminGithubHealth() {
  return request<AdminGithubHealth>("/api/admin/github-health");
}

export interface AdminPatreonReviewCandidate {
  id: string;
  hash: string;
  hash16: string;
  kind: "image" | "gif";
  width: number;
  height: number;
  label: string;
  sourceLabel: string;
  artPath: string;
  previewPath: string;
  posterPath?: string;
  blurPath?: string;
  coverPath?: string;
  selectedBySync?: boolean;
}

export interface AdminPatreonReviewDecision {
  post_id?: string;
  approved?: boolean;
  series?: string;
  series_slug?: string;
  source_series?: string;
  source_series_slug?: string;
  tier_required?: "free" | "paid" | "platinum" | string;
  tags?: string[];
  characters?: string[];
  character_slugs?: string[];
  pairing?: string;
  pairing_slug?: string;
  description_short?: string;
  selected_art_path?: string;
  metadata_confidence?: number;
  metadata_provenance?: Record<string, unknown>;
  warnings?: string[];
  reviewed_at?: string;
  reopened_at?: string;
}

export interface AdminPatreonReviewPost {
  post_id: string;
  title: string;
  publish_date: string;
  safePatreonUrl: string;
  statusText: string;
  suggested_tier_required: "free" | "paid" | "platinum" | string;
  suggestedTierLabel: string;
  suggestedSeriesSlug: string;
  suggestedSeriesTitle: string;
  suggestedSourceSeriesSlug: string;
  suggestedSourceSeriesTitle: string;
  suggestedTags: string[];
  suggestedCharacters: string[];
  suggestedCharacterSlugs: string[];
  suggestedPairing: string;
  suggestedPairingSlug: string;
  suggestedDescriptionShort: string;
  metadataConfidence: number;
  metadataProvenance: Record<string, unknown>;
  warnings: string[];
  slug: string;
  candidateCount: number;
  candidates: AdminPatreonReviewCandidate[];
  decision: AdminPatreonReviewDecision | null;
  diagnostics?: Record<string, unknown>;
}

export interface AdminPatreonReviewQueue {
  configured: boolean;
  storage: string;
  generatedAt: string;
  summary: {
    pendingPosts: number;
    pendingReviewPosts?: number;
    approvedPosts?: number;
    readyToStage?: boolean;
    candidates: number;
    thumbnailsMissing: number;
  };
  tierOptions: Array<{ value: "free" | "paid" | "platinum" | string; label: string }>;
  seriesOptions: Array<{ slug: string; title: string; sourceSeriesSlug: string; sourceSeriesTitle: string }>;
  tagSuggestions: string[];
  posts: AdminPatreonReviewPost[];
}

export function getAdminPatreonReviewQueue() {
  return request<AdminPatreonReviewQueue>("/api/admin/patreon-review");
}

export function saveAdminPatreonReview(csrf: string, body: {
  action: "approve" | "reopen";
  post_id: string;
  selectedArtPath?: string;
  seriesSlug?: string;
  tierRequired?: "free" | "paid" | "platinum";
  tags?: string[];
  characters?: string[];
  characterSlugs?: string[];
  pairing?: string;
  descriptionShort?: string;
  reviewApproved?: boolean;
  dryRun?: boolean;
}) {
  return request<{ saved: boolean; dryRun?: boolean; action: "approve" | "reopen"; post_id: string; queue: AdminPatreonReviewQueue; commitSha?: string | null; commitUrl?: string | null }>("/api/admin/patreon-review", {
    method: "POST",
    headers: { "x-csrf-token": csrf },
    body: JSON.stringify(body)
  });
}

export function triggerAdminPatreonSync(csrf: string, body: { confirm: boolean; dryRun?: boolean; mode?: "dry-run" | "sync" | "sync-deploy" }) {
  return request<AdminPatreonSyncStatus>("/api/admin/patreon-sync", {
    method: "POST",
    headers: { "x-csrf-token": csrf },
    body: JSON.stringify(body)
  });
}

export function saveAdminEpisode(slug: string, csrf: string, body: Partial<CatalogResponse["episodes"][number]>) {
  return request<{ saved: boolean; episode: CatalogResponse["episodes"][number] }>(`/api/admin/episode/${encodeURIComponent(slug)}`, {
    method: "PATCH",
    headers: { "x-csrf-token": csrf },
    body: JSON.stringify(body)
  });
}

export function createAdminSeries(csrf: string, body: Partial<Series>) {
  return request<{ saved: boolean; series: Series }>("/api/admin/series", {
    method: "POST",
    headers: { "x-csrf-token": csrf },
    body: JSON.stringify(body)
  });
}

export function saveAdminSeries(slug: string, csrf: string, body: Partial<Series> & { updateEpisodes?: boolean }) {
  return request<{ saved: boolean; series: Series }>(`/api/admin/series/${encodeURIComponent(slug)}`, {
    method: "PATCH",
    headers: { "x-csrf-token": csrf },
    body: JSON.stringify(body)
  });
}
